/**
 * Speech-adapter: SpeechSynthesis, ontgrendeld na expliciete user gesture,
 * stemmen asynchroon geladen, gedebounced en zonder overlap.
 */

/**
 * De stem leest het gedachtestreepje in de stand ("twee–nul") zonder pauze,
 * alsof het één woord is. Een komma dwingt de pauze af; alleen voor spraak —
 * de getoonde tekst en de eventlog houden het streepje.
 */
function spreekbaar(text: string): string {
  return text.replace(/–/g, ", ");
}

export class SpeechAdapter {
  private voices: SpeechSynthesisVoice[] = [];
  private unlocked = false;
  private queue: string[] = [];
  private speaking = false;
  private lastText = "";

  get supported(): boolean {
    return typeof window !== "undefined" && "speechSynthesis" in window;
  }

  /** Roep aan in een user gesture (setup heeft een "audiotest"-knop). */
  unlock(): void {
    if (!this.supported) return;
    this.unlocked = true;
    const u = new SpeechSynthesisUtterance();
    u.text = " ";
    u.volume = 0;
    window.speechSynthesis.speak(u);
    this.loadVoices();
  }

  loadVoices(): void {
    if (!this.supported) return;
    const sync = () => {
      this.voices = window.speechSynthesis.getVoices();
    };
    sync();
    window.speechSynthesis.onvoiceschanged = sync;
  }

  get voiceCount(): number {
    return this.voices.length;
  }

  private pickVoice(): SpeechSynthesisVoice | null {
    const nl = this.voices.filter((v) => v.lang.toLowerCase().startsWith("nl"));
    return nl.find((v) => v.localService) ?? nl[0] ?? this.voices[0] ?? null;
  }

  /** Spreek een tekst; overlap wordt voorkomen (seriële wachtrij, max 2). */
  say(text: string): void {
    if (!this.supported || !this.unlocked || !text) return;
    if (text === this.lastText && this.queue.length > 0) return; // debounce herhaling
    this.lastText = text;
    if (this.queue.length >= 2) this.queue.shift();
    this.queue.push(text);
    this.drain();
  }

  private drain(): void {
    if (!this.supported || this.speaking || this.queue.length === 0) return;
    const text = this.queue.shift()!;
    const u = new SpeechSynthesisUtterance(spreekbaar(text));
    u.lang = "nl-NL";
    const voice = this.pickVoice();
    if (voice) u.voice = voice;
    u.rate = 1.06;
    this.speaking = true;
    u.onend = () => {
      this.speaking = false;
      this.drain();
    };
    u.onerror = () => {
      this.speaking = false;
      this.drain();
    };
    window.speechSynthesis.speak(u);
  }

  stop(): void {
    if (!this.supported) return;
    window.speechSynthesis.cancel();
    this.queue = [];
    this.speaking = false;
  }
}
